/**
 * SpeechManager - Orchestrates speech recognition with state machine
 * 
 * Manages speech engine lifecycle, state transitions, and provides
 * a clean interface for voice recognition in the application.
 */

import { SpeechStates } from './SpeechEngine.js';
import { BrowserSTT } from './BrowserSTT.js';
import { MockSTT } from './MockSTT.js';
import { VADSpeechEngine } from './VADSpeechEngine.js';
import { SmartSpeechEngine } from './SmartSpeechEngine.js';
import { TriggerDetector } from './TriggerDetector.js';

export class SpeechManager {
    constructor(options = {}) {
        this.options = {
            useMock: options.useMock || false,
            useVAD: options.useVAD !== false, // Enable VAD by default
            useSmartEngine: options.useSmartEngine !== false, // Use smart engine by default
            engine: options.engine || null,
            showAllResults: options.showAllResults || false, // Show all results without trigger phrase
            ...options
        };
        
        this.engine = null;
        this.currentState = SpeechStates.IDLE;
        this.isEnabled = false;
        this.triggerPhrase = 'hey chef';
        this.isTriggered = false;
        this.triggerTimeout = null;
        this.commandBuffer = '';
        this.currentRunId = 0;
        
        // Initialize trigger detector for advanced trigger detection
        this.triggerDetector = new TriggerDetector({
            triggerPhrases: [this.triggerPhrase, 'ok chef', 'chef'],
            similarityThreshold: 0.8,
            requireConsecutiveMatches: true,
            consecutiveMatchCount: 2,
            refractoryPeriod: 3000
        });
        this.setupTriggerDetectorCallbacks();
        
        // Callbacks
        this.onResult = null;
        this.onError = null;
        this.onStateChange = null;
        this.onTrigger = null;
        
        // Lazy initialize engines to avoid mic access on load
        this.initialized = false;
    }

    /**
     * Initialize the speech engine
     */
    initializeEngine() {
        if (this.options.engine) {
            this.engine = this.options.engine;
        } else if (this.options.useSmartEngine) {
            this.engine = new SmartSpeechEngine({
                useMock: this.options.useMock,
                preferredEngines: ['streaming', 'vad', 'browser'],
                // Defer Smart engine initialization until start()
                autoInit: false,
                streamingOptions: {
                    language: this.options.language || 'en-US'
                },
                vadOptions: {
                    language: this.options.language || 'en-US',
                    energyThreshold: this.options.energyThreshold,
                    silenceThreshold: this.options.silenceThreshold,
                    silenceDuration: this.options.silenceDuration,
                    minSpeechDuration: this.options.minSpeechDuration
                },
                browserOptions: {
                    language: this.options.language || 'en-US'
                }
            });
        } else if (this.options.useMock) {
            this.engine = new MockSTT();
        } else if (this.options.useVAD) {
            this.engine = new VADSpeechEngine({
                language: this.options.language || 'en-US',
                energyThreshold: this.options.energyThreshold,
                silenceThreshold: this.options.silenceThreshold,
                silenceDuration: this.options.silenceDuration,
                minSpeechDuration: this.options.minSpeechDuration
            });
        } else {
            this.engine = new BrowserSTT();
        }
        
        this.setupEngineCallbacks();
        this.initialized = true;
    }

    /**
     * Setup engine callbacks
     */
    setupEngineCallbacks() {
        if (!this.engine) return;

        this.engine.setOnPartial((result) => {
            this.handlePartialResult(result);
        });

        this.engine.setOnFinal((result) => {
            this.handleFinalResult(result);
        });

        this.engine.setOnError((error) => {
            this.handleError(error);
        });

        this.engine.setOnStateChange((stateChange) => {
            this.handleStateChange(stateChange);
        });
    }

    /**
     * Setup trigger detector callbacks
     */
    setupTriggerDetectorCallbacks() {
        if (!this.triggerDetector) return;
        
        this.triggerDetector.setOnTriggerDetected((event) => {
            console.log('🎯 SpeechManager: Trigger detected via TriggerDetector:', event.phrase, 'score:', event.score.toFixed(3));
            this.handleTrigger(event.fullText);
        });
        
        this.triggerDetector.setOnTriggerRejected((event) => {
            console.log('🎯 SpeechManager: Trigger rejected:', event.phrase, 'reason:', event.reason, 'score:', event.score.toFixed(3));
        });
    }

    /**
     * Handle partial speech results
     * @param {Object} result - Partial result
     */
    handlePartialResult(result) {
        if (!this.isEnabled) return;
        
        // Feed to trigger detector for advanced detection
        if (this.triggerDetector) {
            this.triggerDetector.processPartialResult(result);
        }
        
        const transcript = result.transcript.toLowerCase();
        
        // Show all results if showAllResults is enabled
        if (this.options.showAllResults) {
            if (this.onResult) {
                this.onResult(result.transcript, false); // false = partial result
            }
        } else {
            // Only process commands if already triggered (no fallback trigger detection)
            if (this.isTriggered) {
                this.commandBuffer = this.stripTrigger(transcript);
            }
        }
    }

    /**
     * Handle final speech results
     * @param {Object} result - Final result
     */
    handleFinalResult(result) {
        if (!this.isEnabled) return;
        
        const transcript = result.transcript.toLowerCase();
        
        // Show all results if showAllResults is enabled
        if (this.options.showAllResults) {
            if (this.onResult) {
                this.onResult(result.transcript, true); // true = final result
            }
        } else {
            // Only process commands if already triggered (no fallback trigger detection)
            if (this.isTriggered) {
                const command = this.stripTrigger(transcript);
                if (command.trim()) {
                    console.log('🎤 SpeechManager: Command detected:', command);
                    if (this.onResult) {
                        this.onResult(command, true);
                    }
                }
                this.resetTriggerState();
            }
        }
    }

    /**
     * Handle trigger phrase detection
     * @param {string} transcript - Full transcript
     */
    handleTrigger(transcript) {
        this.isTriggered = true;
        this.commandBuffer = '';
        
        console.log('🎤 SpeechManager: Trigger detected:', this.triggerPhrase);
        
        if (this.onTrigger) {
            this.onTrigger(transcript);
        }
        
        // Set timeout for command input
        this.triggerTimeout = setTimeout(() => {
            this.resetTriggerState();
        }, 5000);
    }

    /**
     * Handle errors
     * @param {Object} error - Error object
     */
    handleError(error) {
        console.error('🎤 SpeechManager: Error:', error);
        
        if (this.onError) {
            this.onError(error);
        }
    }

    /**
     * Handle state changes
     * @param {Object} stateChange - State change object
     */
    handleStateChange(stateChange) {
        this.currentState = stateChange.current;
        
        console.log('🎤 SpeechManager: State changed:', stateChange);
        
        if (this.onStateChange) {
            this.onStateChange(stateChange);
        }
    }

    /**
     * Strip trigger phrase from transcript
     * @param {string} transcript - Full transcript
     * @returns {string} Command text
     */
    stripTrigger(transcript) {
        const index = transcript.indexOf(this.triggerPhrase);
        if (index >= 0) {
            return transcript.substring(index + this.triggerPhrase.length).trim();
        }
        return transcript;
    }

    /**
     * Reset trigger state
     */
    resetTriggerState() {
        this.isTriggered = false;
        this.commandBuffer = '';
        if (this.triggerTimeout) {
            clearTimeout(this.triggerTimeout);
            this.triggerTimeout = null;
        }
    }

    /**
     * Start speech recognition
     * @returns {Promise<boolean>} Success status
     */
    async start() {
        if (!this.initialized) {
            this.initializeEngine();
        }
        if (!this.engine) {
            console.error('🎤 SpeechManager: No engine available');
            return false;
        }

        // Start new run
        this.currentRunId++;
        const runId = this.currentRunId;
        
        this.isEnabled = true;
        this.resetTriggerState();
        
        if (process.env.NODE_ENV === 'development') console.log(`🎤 SpeechManager: Starting speech recognition (runId: ${runId})...`);
        
        try {
            // Idempotent: if underlying engine is already ready/listening, no-op
            try {
                if (this.engine.getState && (this.engine.getState() === 'ready' || this.engine.getState() === 'listening')) {
                    return true;
                }
            } catch (_) {}

            // Ensure permission + AC before ASR
            try {
                const mic = await import('./MicManager.js');
                await mic.default.preflight();
                if ((await mic.default.getPermission()) !== 'granted') {
                    if (this.emit) { try { this.emit('mic:denied'); } catch (_) {} }
                    return false;
                }
                await mic.default.acquire();
                await mic.default.resume();
            } catch (_) {}
            const result = await this.engine.start();
            // Check if this is still the current run
            if (runId !== this.currentRunId) {
                if (process.env.NODE_ENV === 'development') console.log(`🎤 SpeechManager: Late start completion ignored (runId: ${runId}, current: ${this.currentRunId})`);
                return false;
            }
            return result;
        } catch (error) {
            if (runId !== this.currentRunId) {
                if (process.env.NODE_ENV === 'development') console.log(`🎤 SpeechManager: Late start error ignored (runId: ${runId}, current: ${this.currentRunId})`);
                return false;
            }
            throw error;
        }
    }

    /**
     * Enable or disable wake mode (UI-facing signal)
     */
    setWakeMode(enabled) {
        this.kwsEnabled = !!enabled;
        if (this.emit) {
            try { this.emit(enabled ? 'kws:armed' : 'kws:off'); } catch (_) {}
        }
    }

    /**
     * Stop speech recognition
     * @returns {Promise<boolean>} Success status
     */
    async stop() {
        if (!this.engine) {
            return false;
        }

        this.isEnabled = false;
        this.resetTriggerState();
        
        console.log('🎤 SpeechManager: Stopping speech recognition...');
        return await this.engine.stop();
    }

    /**
     * Hard stop - guaranteed mic off and tear-down
     * @param {string} reason - Reason for hard stop
     * @returns {Promise<boolean>} Success status
     */
    async hardStop(reason = 'hard_stop') {
        if (process.env.NODE_ENV === 'development') console.log(`🎤 SpeechManager: Hard stop requested (${reason})`);
        
        try {
            // Increment runId to invalidate any pending operations
            this.currentRunId++;
            
            // Cancel any in-flight operations
            if (this.abortController) {
                this.abortController.abort();
                this.abortController = null;
            }
            
            // Stop engine and await fully stopped
            if (this.engine) {
                await this.engine.stop();
                await this.engine.destroy();
                this.engine = null;
            }
            
            // Release all microphone resources
            const micManager = await import('./MicManager.js');
            await micManager.default.releaseAll();
            
            // Reset all state
            this.isEnabled = false;
            this.currentState = SpeechStates.IDLE;
            this.resetTriggerState();
            
            // Reset trigger detector
            if (this.triggerDetector) {
                this.triggerDetector.reset();
            }
            
            if (process.env.NODE_ENV === 'development') console.log(`🎤 SpeechManager: Hard stop completed - mic off, resources released (runId: ${this.currentRunId})`);
            return true;
        } catch (error) {
            console.error('🎤 SpeechManager: Error during hard stop:', error);
            return false;
        }
    }

    /**
     * Check if currently listening
     * @returns {boolean} Listening status
     */
    isListening() {
        return this.engine ? this.engine.isListening() : false;
    }

    /**
     * Get current state
     * @returns {string} Current state
     */
    getState() {
        return this.engine ? this.engine.getState() : SpeechStates.IDLE;
    }

    /**
     * Check if speech recognition is supported
     * @returns {boolean} Support status
     */
    isSupported() {
        return this.engine ? this.engine.isSupported() : false;
    }

    /**
     * Get browser compatibility info
     * @returns {Object} Compatibility info
     */
    getBrowserInfo() {
        return this.engine ? this.engine.getBrowserInfo() : { isSupported: false };
    }

    /**
     * Update trigger phrase
     * @param {string} phrase - New trigger phrase
     */
    updateTriggerPhrase(phrase) {
        this.triggerPhrase = phrase.toLowerCase();
        console.log('🎤 SpeechManager: Trigger phrase updated to:', this.triggerPhrase);
    }

    /**
     * Set result callback
     * @param {Function} callback - Callback function
     */
    setOnResult(callback) {
        this.onResult = callback;
    }

    /**
     * Set error callback
     * @param {Function} callback - Callback function
     */
    setOnError(callback) {
        this.onError = callback;
    }

    /**
     * Set state change callback
     * @param {Function} callback - Callback function
     */
    setOnStateChange(callback) {
        this.onStateChange = callback;
    }

    /**
     * Set trigger callback
     * @param {Function} callback - Callback function
     */
    setOnTrigger(callback) {
        this.onTrigger = callback;
    }

    /**
     * Get engine instance (for testing)
     * @returns {Object} Engine instance
     */
    getEngine() {
        return this.engine;
    }

    /**
     * Switch to mock engine (for testing)
     */
    useMockEngine() {
        this.stop();
        this.engine = new MockSTT();
        this.setupEngineCallbacks();
    }

    /**
     * Switch to browser engine
     */
    useBrowserEngine() {
        this.stop();
        this.engine = new BrowserSTT();
        this.setupEngineCallbacks();
    }

    /**
     * Switch to VAD engine
     */
    useVADEngine(options = {}) {
        this.stop();
        this.engine = new VADSpeechEngine({
            language: this.options.language || 'en-US',
            energyThreshold: options.energyThreshold || this.options.energyThreshold,
            silenceThreshold: options.silenceThreshold || this.options.silenceThreshold,
            silenceDuration: options.silenceDuration || this.options.silenceDuration,
            minSpeechDuration: options.minSpeechDuration || this.options.minSpeechDuration
        });
        this.setupEngineCallbacks();
    }

    /**
     * Update VAD thresholds
     * @param {Object} thresholds - New threshold values
     */
    updateVADThresholds(thresholds) {
        if (this.engine && this.engine.updateVADThresholds) {
            this.engine.updateVADThresholds(thresholds);
        }
        
        // Update options for future engine creation
        Object.assign(this.options, thresholds);
    }

    /**
     * Get VAD state
     * @returns {Object} VAD state
     */
    getVADState() {
        return this.engine && this.engine.getVADState ? this.engine.getVADState() : null;
    }

    /**
     * Check if using VAD engine
     * @returns {boolean} VAD engine status
     */
    isUsingVAD() {
        return this.engine && this.engine.constructor.name === 'VADSpeechEngine';
    }

    /**
     * Check if using smart engine
     * @returns {boolean} Smart engine status
     */
    isUsingSmartEngine() {
        return this.engine && this.engine.constructor.name === 'SmartSpeechEngine';
    }

    /**
     * Get current engine type
     * @returns {string} Current engine type
     */
    getCurrentEngineType() {
        if (!this.engine) return 'none';
        
        if (this.isUsingSmartEngine()) {
            return this.engine.getCurrentEngineType();
        }
        
        const engineMap = {
            'StreamingSTT': 'streaming',
            'VADSpeechEngine': 'vad',
            'BrowserSTT': 'browser',
            'MockSTT': 'mock'
        };
        
        return engineMap[this.engine.constructor.name] || 'unknown';
    }

    /**
     * Get engine statistics
     * @returns {Object} Engine statistics
     */
    getEngineStats() {
        if (this.isUsingSmartEngine()) {
            return this.engine.getEngineStats();
        }
        
        return {
            currentEngine: this.getCurrentEngineType(),
            availableEngines: 1,
            engineHistory: [],
            fallbackEnabled: false
        };
    }

    /**
     * Switch to specific engine (smart engine only)
     * @param {string} engineType - Engine type to switch to
     */
    async switchEngine(engineType) {
        if (this.isUsingSmartEngine()) {
            return await this.engine.switchEngine(engineType);
        }
        
        console.warn('🎤 SpeechManager: Engine switching only available with smart engine');
        return false;
    }

    /**
     * Cleanup resources
     */
    destroy() {
        this.stop();
        if (this.engine) {
            this.engine.destroy();
        }
        this.resetTriggerState();
        this.onResult = null;
        this.onError = null;
        this.onStateChange = null;
        this.onTrigger = null;
    }
}
